import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  message,
  Space,
  Select
} from 'antd';
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { baseDataService, membershipService } from '../../../services';
import {DropdownResponse, EnglishMasterTabProps, MemberUpdateItem, MemGetMemberResponse} from "../../../model";

const { Option } = Select;

const EnglishMasterTab: React.FC<EnglishMasterTabProps> = ({
  memberData,
  onDataChanged,
  changedFields,
  onSaveSuccess,
  isEditing,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [memberLevelsEn, setMemberLevelsEn] = useState<DropdownResponse[] | null>([]);

  // 載入下拉選單資料
  useEffect(() => {
    const fetchDropdownData = async () => {
      // 載入會員級數（英文）
      const levelData = await baseDataService.getDropdown({ paramType: 'member_level_en' });
      setMemberLevelsEn(levelData);
    };
    
    fetchDropdownData();
  }, []);

  // 初始化表單值
  React.useEffect(() => {
    form.setFieldsValue({
      ...memberData,
    });
  }, [memberData, form]);

  // 處理表單欄位變更
  const handleFieldsChange = (changedFields: any, allFields: any) => {
    if (!isEditing) return;
    
    // 確認每個有變更的欄位
    changedFields.forEach((field: any) => {
      const fieldName = field.name[0];
      const fieldValue = field.value;
      
      const originalValue = memberData[fieldName as keyof MemGetMemberResponse];
      
      // 處理 undefined 和 null
      onDataChanged(fieldName, 
        (originalValue === null || originalValue === undefined) 
          ? !!fieldValue 
          : fieldValue !== originalValue
      );
    });
  };

  // 儲存變更
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      // 如果沒有變更欄位，直接返回
      if (changedFields.size === 0) {
        message.info('沒有需要儲存的變更');
        return;
      }
      
      setLoading(true);
      
      // 準備更新資料
      const updateData: MemberUpdateItem = {
        membershipId: memberData.membershipId,
        memberId: memberData.memberId,
        companyName: memberData.companyName,
        companyNameEn: values.companyNameEn,
        contactAddressEn: values.contactAddressEn,
        factoryAddressEn: values.factoryAddressEn,
        responsiblePersonEn: values.responsiblePersonEn,
        chairmanEn: values.chairmanEn,
        generalManagerEn: values.generalManagerEn,
        membershipLevelEn: values.membershipLevelEn,
      };

      // 儲存資料到後端
      const response = await membershipService.updateMember(
          {
            members: [
              {
                ...memberData,
                ...updateData
              }]
          }
      );
      
      if (response && response.updatedCount > 0) {
        message.success('會員英文資料已更新成功');
        onSaveSuccess();
      } else if (response) {
        message.error('會員英文資料更新失敗');
      }
    } catch (error) {
      console.error('Update member English data failed:', error);
      message.error('會員英文資料更新發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  // 重置表單
  const handleReset = () => {
    form.setFieldsValue({
      ...memberData,
    });
    
    // 清除所有變更標記
    changedFields.forEach(field => {
      onDataChanged(field, false);
    });
  };

  // 判斷欄位是否有變更
  const isFieldChanged = (fieldName: string) => {
    return changedFields.has(fieldName);
  };

  return (
    <Card>
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onFieldsChange={handleFieldsChange}
        initialValues={{
          ...memberData,
        }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="Company Name" 
              name="companyNameEn"
              validateStatus={isFieldChanged('companyNameEn') ? 'warning' : ''}
              help={isFieldChanged('companyNameEn') ? '此欄位已修改' : ''}
            >
              <Input placeholder="Please enter the company name in English" disabled={!isEditing} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <Form.Item
              labelCol={{ span: 4 }}
              wrapperCol={{ span: 20 }}
              label="Company(Office) Address" 
              validateStatus={isFieldChanged('contactAddressEn') || isFieldChanged('contactZip') ? 'warning' : ''}
              help={isFieldChanged('contactAddressEn') || isFieldChanged('contactZip') ? '此欄位已修改' : ''}
            >
              <Input.Group compact>
                <Form.Item name="contactZip" noStyle>
                  <Input 
                    style={{ width: '20%' }} 
                    placeholder="ZIP code" 
                    disabled={!isEditing}
                  />
                </Form.Item>
                <Form.Item name="contactAddressEn" noStyle>
                  <Input 
                    style={{ width: '80%' }} 
                    placeholder="Address" 
                    disabled={!isEditing}
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </Col>
        </Row>

          <Row gutter={[24, 16]}>
              <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                  <Form.Item
                      labelCol={{ span: 4 }}
                      wrapperCol={{ span: 20 }}
                      label="Factory Address"
                      validateStatus={isFieldChanged('factoryAddressEn') || isFieldChanged('factoryZip') ? 'warning' : ''}
                      help={isFieldChanged('factoryAddressEn') || isFieldChanged('factoryZip') ? '此欄位已修改' : ''}
                  >
                      <Input.Group compact>
                          <Form.Item name="factoryZip" noStyle>
                              <Input
                                  style={{ width: '20%' }}
                                  placeholder="ZIP code"
                                  disabled={!isEditing}
                              />
                          </Form.Item>
                          <Form.Item name="factoryAddressEn" noStyle>
                              <Input
                                  style={{ width: '80%' }}
                                  placeholder="Address"
                                  disabled={!isEditing}
                              />
                          </Form.Item>
                      </Input.Group>
                  </Form.Item>
              </Col>
          </Row>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="Owner" 
              name="responsiblePersonEn"
              validateStatus={isFieldChanged('responsiblePersonEn') ? 'warning' : ''}
              help={isFieldChanged('responsiblePersonEn') ? '此欄位已修改' : ''}
            >
              <Input placeholder="Please enter the chairman's name in English" disabled={!isEditing} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="President" 
              name="chairmanEn"
              validateStatus={isFieldChanged('chairmanEn') ? 'warning' : ''}
              help={isFieldChanged('chairmanEn') ? '此欄位已修改' : ''}
            >
              <Input placeholder="Please enter the chairman's name in English" disabled={!isEditing} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="General Manager" 
              name="generalManagerEn"
              validateStatus={isFieldChanged('generalManagerEn') ? 'warning' : ''}
              help={isFieldChanged('generalManagerEn') ? '此欄位已修改' : ''}
            >
              <Input placeholder="Please enter the general manager's name in English" disabled={!isEditing} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item
              label="Tami Class"
              name="membershipLevelEn"
              validateStatus={isFieldChanged('membershipLevelEn') ? 'warning' : ''}
              help={isFieldChanged('membershipLevelEn') ? '此欄位已修改' : ''}
            >
              <Select placeholder="Please select membership level" disabled>
                {memberLevelsEn?.map(level => (
                  <Option key={level.paramCode} value={level.paramCode}>
                    {level.paramValue}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="Capital" 
              name="capitalAmount"
              validateStatus={isFieldChanged('capitalAmount') ? 'warning' : ''}
              help={isFieldChanged('capitalAmount') ? '此欄位已修改' : ''}
            >
              <InputNumber
                style={{ width: '100%' }}
                disabled={!isEditing}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Form.Item 
              label="No. of Employees" 
              name="employeeCount"
              validateStatus={isFieldChanged('employeeCount') ? 'warning' : ''}
              help={isFieldChanged('employeeCount') ? '此欄位已修改' : ''}
            >
              <Input disabled={!isEditing} />
            </Form.Item>
          </Col>
        </Row>

        {isEditing && (
          <Row justify="center" style={{ marginTop: 24 }}>
            <Space>
              <Button 
                icon={<UndoOutlined />} 
                onClick={handleReset}
                disabled={changedFields.size === 0}
              >
                重置
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                onClick={handleSave}
                loading={loading}
                disabled={changedFields.size === 0}
              >
                儲存變更
              </Button>
            </Space>
          </Row>
        )}
      </Form>
    </Card>
  );
};

export default EnglishMasterTab;
